import { createSlice, createSelector } from "@reduxjs/toolkit";
import { backendConnector } from "../backendIntegration/lambdas";
import { createApiThunk, addAsyncCases } from "./sliceHelpers";

// The answer review loop: "N answers to review" on the deck, the full-screen queue behind it, and
// a per-application map (jobUrl -> jobKey + pending) that drives the My Jobs dot and the "What we
// wrote" tab. The backend decides what is eligible (letters first, never-asked classifications
// next) and serves it a page at a time; this slice spends `pending` one verdict at a time and
// refills from the next page until it hits zero.
//
// `pending` is the number on the pill, so it only ever moves when the user actually judges
// something. An earlier cut stopped after one batch of three and hid the pill for the rest of the
// session, which read as "I reviewed one and the other nine disappeared".

export const fetchPendingAnswers = createApiThunk(
  "answerReviews/fetchPending",
  () => backendConnector.getPendingAnswers(),
  { logMessage: "Error fetching pending answers:", errorMessage: "Failed to load answers to review" }
);

export const submitAnswerReview = createApiThunk(
  "answerReviews/submit",
  ({ jobKey, answerId, review }) => backendConnector.reviewAnswer(jobKey, answerId, review),
  { logMessage: "Error saving answer review:", errorMessage: "Failed to save your review" }
);

const initialState = {
  pending: 0,
  batch: [],
  applications: [],
  loading: false,
  loaded: false,
  error: null,
  // Its own field, not `error`: `error` is shared with the review submit, so a failed verdict
  // would read as "the application map did not load" and a successful one would hide a map
  // failure. This one is about the jobUrl -> jobKey map only, and the Reply and Prep surfaces
  // key their retry off it.
  applicationsError: null,
  queueOpen: false,
  submitting: false,
  // Verdicts given since the queue was opened — the numerator of "Answer 4 of 12". Reset per
  // visit, so reopening a pill that says 3 starts at "Answer 1 of 3" rather than carrying the
  // last sitting's tally into a header the pill does not agree with.
  reviewedThisVisit: 0,
  // Entry ids judged this session. The review write and the pending read are separate round
  // trips, so a refetch can still carry one; re-serving an answer the user just judged is the
  // most confusing thing the queue can do.
  reviewedIds: [],
};

export const entryId = (e) => `${e.jobKey}:${e.answerId}`;

/** Pure: state after a verdict on the head of the batch. One verdict, one off the count. */
export const afterReview = (state, { jobKey, answerId }) => {
  const [head, ...rest] = state.batch;
  if (!head || head.jobKey !== jobKey || (answerId !== undefined && head.answerId !== answerId)) return state;
  const applications = state.applications.map((a) =>
    a.jobKey === jobKey ? { ...a, pending: Math.max(0, (a.pending ?? 0) - 1) } : a
  );
  return {
    ...state,
    batch: rest,
    pending: Math.max(0, state.pending - 1),
    applications,
    reviewedThisVisit: state.reviewedThisVisit + 1,
    reviewedIds: [...state.reviewedIds, entryId(head)],
  };
};

const answerReviewsSlice = createSlice({
  name: "answerReviews",
  initialState,
  reducers: {
    openReviewQueue(state) {
      state.queueOpen = true;
      state.reviewedThisVisit = 0;
    },
    closeReviewQueue(state) {
      state.queueOpen = false;
    },
    clearAnswerReviews() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases(builder, fetchPendingAnswers, {
      loadingKey: "loading",
      loadedKey: "loaded",
      onPending: (state) => {
        state.applicationsError = null;
      },
      onRejected: (state, action) => {
        state.error = action.payload;
        state.applicationsError = action.payload || "Failed to load answers to review";
      },
      onFulfilled: (state, action) => {
        const { pending = 0, batch = [], applications = [] } = action.payload || {};
        // Drop anything judged this session, and — while the queue is open — keep the card the
        // user is looking at at the front, so a My Jobs refetch cannot swap it out mid-verdict.
        const judged = new Set(state.reviewedIds);
        const fresh = batch.filter((e) => !judged.has(entryId(e)));
        const current = state.batch[0];
        state.batch =
          state.queueOpen && current && !judged.has(entryId(current))
            ? [current, ...fresh.filter((e) => entryId(e) !== entryId(current))]
            : fresh;
        // A page with answers still pending but nothing on it we have not already judged is a
        // stale read — the verdict write and this read are separate round trips. Taking its
        // counts would walk the pill back up to its opening number after a few verdicts, which
        // is the exact confusion this all started as. Keep what we know instead.
        if (fresh.length === 0 && pending > 0) return;
        state.pending = pending;
        state.applications = applications;
      },
    });
    builder
      .addCase(submitAnswerReview.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(submitAnswerReview.fulfilled, (state, action) => {
        state.submitting = false;
        Object.assign(state, afterReview(state, action.meta.arg));
      })
      .addCase(submitAnswerReview.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });
  },
});

export const { openReviewQueue, closeReviewQueue, clearAnswerReviews } = answerReviewsSlice.actions;

const EMPTY_APPLICATIONS = [];
const selectApplications = (state) => state.answerReviews?.applications || EMPTY_APPLICATIONS;

/** jobUrl -> { jobKey, pending } for the My Jobs dot and the archive tab. Memoized: the job list
 *  renders this per card, so a fresh object per store change would re-render every card on every action. */
export const selectAnswersByJobUrl = createSelector([selectApplications], (applications) =>
  Object.fromEntries(applications.map((a) => [a.jobUrl, a]))
);

/** The pill shows for as long as anything is left, and its number is what the queue will serve.
 *  Deliberately not gated on `loaded`: that flips false for the duration of every refetch, and the
 *  pill blinking out mid-refill is worse than showing a count that is a moment old. */
export const selectShowReviewPill = (state) => state.answerReviews?.pending > 0;

/** "Answer `position` of `total`" — a denominator that holds still while the queue pages. */
export const selectQueueProgress = createSelector(
  [(state) => state.answerReviews?.reviewedThisVisit || 0, (state) => state.answerReviews?.pending || 0],
  (reviewed, pending) => ({ position: reviewed + 1, total: Math.max(reviewed + pending, 1) })
);

export default answerReviewsSlice.reducer;
